import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageSquare, Send, ArrowLeft, Ban, ExternalLink, ShieldAlert } from "lucide-react";

export default function MessagesInbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [conv, setConv] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    api.get("/me/conversations").then((r) => setThreads(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (t) => {
    setActive(t); setConv(null);
    const { data } = await api.get(`/me/conversations/${t.id}`);
    setConv(data);
    setThreads((ts) => ts.map((x) => (x.id === t.id ? { ...x, unread: 0 } : x)));
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const send = async () => {
    const msg = draft.trim();
    if (!msg) return;
    setDraft("");
    try {
      await api.post(`/me/conversations/${active.id}/message`, { message: msg });
      const { data } = await api.get(`/me/conversations/${active.id}`);
      setConv(data);
      loadThreads();
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); setDraft(msg); }
  };

  const block = async () => {
    if (!window.confirm("Bloquer cette conversation ? Vous ne pourrez plus échanger de messages.")) return;
    await api.post(`/me/conversations/${active.id}/block`);
    toast.success("Conversation bloquée");
    openThread(active); loadThreads();
  };

  return (
    <div data-testid="messages-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2 mb-4"><MessageSquare className="text-[#E05638]" size={24} /> Mes messages</h1>
      <div className="grid md:grid-cols-3 gap-4">
        {/* Liste des conversations */}
        <div className={`md:col-span-1 ${active ? "hidden md:block" : ""}`} data-testid="threads-list">
          {loading && <p className="text-slate-500 text-sm">Chargement…</p>}
          {!loading && threads.length === 0 && (
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">
              Aucune conversation pour l'instant.
            </div>
          )}
          <div className="space-y-2">
            {threads.map((t) => (
              <button key={t.id} onClick={() => openThread(t)} data-testid={`thread-${t.id}`}
                      className={`w-full text-left bg-white border rounded-xl p-3 transition-colors ${active?.id === t.id ? "border-[#E05638]" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-900 text-sm truncate">{t.other_name}</span>
                  {t.unread > 0 && <span className="shrink-0 bg-[#E05638] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid={`thread-unread-${t.id}`}>{t.unread}</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{t.annonce_title}</div>
                <div className="text-xs text-slate-400 truncate mt-0.5">{t.last_message || "—"}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation active */}
        <div className={`md:col-span-2 ${active ? "" : "hidden md:block"}`}>
          {!active ? (
            <div className="hidden md:flex items-center justify-center h-full text-slate-400 text-sm bg-white border border-slate-200 rounded-2xl p-10">
              Sélectionnez une conversation
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl flex flex-col h-[70vh]" data-testid="conversation-view">
              <div className="flex items-center justify-between gap-2 p-3 border-b border-slate-100">
                <button onClick={() => setActive(null)} className="md:hidden text-slate-500" data-testid="conv-back"><ArrowLeft size={18} /></button>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{conv?.thread?.other_name}</div>
                  <Link to={`/petites-annonces/annonce/${conv?.thread?.annonce_slug}`} className="text-xs text-[#E05638] hover:underline inline-flex items-center gap-1 truncate">
                    {conv?.thread?.annonce_title} <ExternalLink size={11} />
                  </Link>
                </div>
                {!conv?.thread?.blocked && (
                  <button onClick={block} className="text-xs text-slate-400 hover:text-rose-600 inline-flex items-center gap-1 shrink-0" data-testid="conv-block"><Ban size={13} /> Bloquer</button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="conv-messages">
                {conv?.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "bg-[#E05638] text-white" : "bg-slate-100 text-slate-800"}`}>
                      <div className="whitespace-pre-line">{m.message}</div>
                      <div className={`text-[10px] mt-0.5 ${m.mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              {conv?.thread?.blocked ? (
                <div className="p-3 border-t border-slate-100 text-xs text-slate-400 flex items-center gap-1"><ShieldAlert size={13} /> Conversation bloquée.</div>
              ) : (
                <div className="p-3 border-t border-slate-100 flex gap-2">
                  <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message…" data-testid="conv-input" />
                  <Button onClick={send} className="btn-shine text-white shrink-0" data-testid="conv-send"><Send size={15} /></Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
